import React, { useState, useEffect } from "react";
import DashboardLayout from "../layouts/DashboardLayout";
import { usePostHog } from 'posthog-js/react';
import { captureEvent, captureError } from "../utils/posthogUtils";
import "./MyProfile.css";
import getApiUrl from "../utils/api";
import {
  FaEnvelope,
  FaBuilding,
  FaUser,
  FaEdit,
  FaSave,
  FaTimes,
  FaBriefcase,
  FaIdCard,
} from "react-icons/fa";

const getInitials = (first, last) => {
  if (!first && !last) return "?";
  return `${(first || "").charAt(0)}${(last || "").charAt(0)}`.toUpperCase();
};

const formatDateForInput = (value) => {
  if (!value) return "";
  try {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return String(value).slice(0, 10);
    }
    return date.toISOString().slice(0, 10);
  } catch {
    return String(value).slice(0, 10);
  }
};

const normalizeProfileSource = (source = {}) => {
  const safe = source || {};
  const getValue = (...keys) => {
    for (const key of keys) {
      if (safe[key] !== undefined && safe[key] !== null) {
        return safe[key];
      }
    }
    return "";
  };

  const getIdValue = (...keys) => {
    for (const key of keys) {
      if (safe[key] !== undefined && safe[key] !== null) {
        return safe[key];
      }
    }
    return null;
  };

  return {
    first_name: getValue("first_name", "FirstName"),
    last_name: getValue("last_name", "LastName"),
    dob: getValue("dob", "DOB", "dateOfBirth"),
    gender: getValue("gender", "Gender"),
    joining_date: getValue("joining_date", "JoiningDate"),
    location: getValue("location", "Location"),
    contact_no: getValue("contact_no", "ContactNo"),
    skills: getValue("skills", "Skills"),
    email: getValue("email", "Email"),
    employee_code: getValue("employee_code", "EmpCode"),
    department_id: getIdValue("department_id", "departmentId", "DepartmentId"),
    designation_id: getIdValue(
      "designation_id",
      "designationId",
      "DesignationId"
    ),
  };
};

const toIdString = (value) => {
  if (value === null || value === undefined || value === "") {
    return "";
  }
  return String(value);
};

const buildFormState = (source = {}, { skipNormalize = false } = {}) => {
  const normalized = skipNormalize ? source : normalizeProfileSource(source);
  return {
    first_name: normalized.first_name || "",
    last_name: normalized.last_name || "",
    dob: formatDateForInput(normalized.dob),
    gender: normalized.gender || "",
    joining_date: formatDateForInput(normalized.joining_date),
    location: normalized.location || "",
    contact_no: normalized.contact_no || "",
    skills: normalized.skills || "",
    department_id: toIdString(normalized.department_id),
    designation_id: toIdString(normalized.designation_id),
  };
};

const parseJsonOrThrow = async (response, context = "response") => {
  const contentType = response.headers?.get("content-type") || "";
  const text = await response.text();

  if (!text) {
    return {};
  }

  if (!contentType.toLowerCase().includes("application/json")) {
    const snippet = text.slice(0, 160).replace(/\s+/g, " ").trim();
    console.error(`Unexpected ${context} format`, {
      status: response.status,
      contentType,
      preview: snippet,
    });
    throw new Error(
      `Unexpected ${context} format (status ${response.status}). Please try again later.`
    );
  }

  try {
    return JSON.parse(text);
  } catch (err) {
    const snippet = text.slice(0, 160).replace(/\s+/g, " ").trim();
    console.error(`Unable to parse ${context} JSON`, {
      status: response.status,
      contentType,
      preview: snippet,
      error: err,
    });
    throw new Error(
      `Unable to parse ${context} data (status ${response.status}). Please try again later.`
    );
  }
};

const MyProfile = () => {
  const [profile, setProfile] = useState(null);
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    dob: "",
    gender: "",
    joining_date: "",
    location: "",
    contact_no: "",
    skills: "",
    department_id: "",
    designation_id: "",
  });
  const [sessionUser, setSessionUser] = useState(null);
  const [initials, setInitials] = useState("?");
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [designations, setDesignations] = useState([]);
  const posthog = usePostHog();

  const loadProfile = async (empCode, fallbackSource = {}) => {
    const fallbackNormalized = normalizeProfileSource({
      ...fallbackSource,
      employee_code: fallbackSource.employee_code || empCode,
    });

    let apiProfile = null;
    let apiNormalized = {};

    try {
      const res = await fetch(getApiUrl(`api/profile/details/${empCode}`), {
        credentials: "include",
      });

      if (res.status === 404) {
        setMessage(
          "We couldn't find saved profile details yet. Feel free to add them below."
        );
        setMessageType("info");
      } else {
        const payload = await parseJsonOrThrow(res, "profile details");

        if (!res.ok || payload?.success === false) {
          const errMsg =
            (payload && payload.message) ||
            `Failed to load profile details (status ${res.status})`;
          throw new Error(errMsg);
        }

        apiProfile = payload?.profile || null;
        if (!apiProfile) {
          apiProfile = payload;
        }
        apiNormalized = normalizeProfileSource(apiProfile);
        setMessage("");
        setMessageType(null);
      }
    } catch (err) {
      console.error("Profile fetch error:", err);
      setMessage(err.message || "Failed to load profile details.");
      setMessageType("error");
    }

    const mergedNormalized = apiProfile
      ? { ...fallbackNormalized, ...apiNormalized }
      : fallbackNormalized;

    const combinedProfile = {
      ...(apiProfile || {}),
      email: mergedNormalized.email || "",
      employee_code: mergedNormalized.employee_code || empCode,
      department_id:
        mergedNormalized.department_id !== undefined &&
          mergedNormalized.department_id !== null &&
          mergedNormalized.department_id !== ""
          ? mergedNormalized.department_id
          : null,
      designation_id:
        mergedNormalized.designation_id !== undefined &&
          mergedNormalized.designation_id !== null &&
          mergedNormalized.designation_id !== ""
          ? mergedNormalized.designation_id
          : null,
    };

    setProfile(combinedProfile);
    const nextForm = buildFormState(mergedNormalized, { skipNormalize: true });
    setFormData(nextForm);
    setInitials(getInitials(nextForm.first_name, nextForm.last_name));

    return combinedProfile;
  };

  const loadReferenceData = async () => {
    const ensureArray = (value) => {
      if (Array.isArray(value)) return value;
      if (Array.isArray(value?.data)) return value.data;
      if (Array.isArray(value?.items)) return value.items;
      if (Array.isArray(value?.results)) return value.results;
      if (Array.isArray(value?.rows)) return value.rows;
      return [];
    };

    try {
      const deptRes = await fetch(getApiUrl("api/auth/departments"), {
        credentials: "include",
      });

      if (deptRes.ok) {
        const deptPayload = await parseJsonOrThrow(deptRes, "departments list");
        setDepartments(ensureArray(deptPayload));
      } else {
        console.warn(`Failed to load departments (status ${deptRes.status})`);
      }
    } catch (err) {
      console.warn("Department list fetch failed:", err.message);
    }

    try {
      const designationRes = await fetch(getApiUrl("api/auth/designations"), {
        credentials: "include",
      });

      if (designationRes.ok) {
        const designationPayload = await parseJsonOrThrow(
          designationRes,
          "designations list"
        );
        setDesignations(ensureArray(designationPayload));
      } else {
        console.warn(
          `Failed to load designations (status ${designationRes.status})`
        );
      }
    } catch (err) {
      console.warn("Designation list fetch failed:", err.message);
    }
  };

  useEffect(() => {
    const fetchUserAndProfile = async () => {
      try {
        setLoading(true);
        setMessage("");
        setMessageType(null);

        const userRes = await fetch(getApiUrl("api/auth/me"), {
          credentials: "include",
        });
        const userData = await userRes.json();
        if (!userRes.ok || !userData.user) {
          throw new Error("Unable to load user session");
        }

        setSessionUser(userData.user);

        const sessionFallback = normalizeProfileSource({
          ...userData.user,
          email: userData.user.email,
          employee_code: userData.user.EmpCode,
          department_id: userData.user.department_id,
          designation_id:
            userData.user.designation_id ||
            userData.user.designationId ||
            userData.user.DesignationId,
          contact_no: userData.user.ContactNo,
          location: userData.user.Location,
        });

        await loadProfile(userData.user.EmpCode, sessionFallback);
      } catch (err) {
        console.error("Profile load error:", err);
        setMessage(err.message || "Failed to load profile");
        setMessageType("error");
        setProfile({ employee_code: "", email: "" });
        const emptyForm = buildFormState({});
        setFormData(emptyForm);
        setInitials(getInitials(emptyForm.first_name, emptyForm.last_name));
      } finally {
        await loadReferenceData();
        setLoading(false);
      }
    };

    fetchUserAndProfile();
  }, []);

  const handleEdit = () => {
    setEditMode(true);
    setMessage("");
    setMessageType(null);
  };

  const handleChange = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    if (!profile && !sessionUser) {
      setMessage("Cannot determine profile details for save.");
      setMessageType("error");
      return;
    }

    setSaving(true);
    setMessage("");
    setMessageType(null);

    const employeeCode =
      profile?.employee_code ||
      sessionUser?.EmpCode ||
      sessionUser?.employee_code;

    if (!employeeCode) {
      setMessage("Unable to determine employee code for this profile.");
      setMessageType("error");
      setSaving(false);
      return;
    }

    const payload = Object.entries(formData).reduce(
      (acc, [key, value]) => {
        acc[key] = value !== "" ? value : null;
        return acc;
      },
      { EmpCode: employeeCode }
    );

    try {
      const response = await fetch(getApiUrl("api/profile/save-details"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        credentials: "include",
      });
      const data = await parseJsonOrThrow(response, "profile save");

      if (!response.ok || data?.success === false) {
        throw new Error(data.message || "Failed to save profile details");
      }

      await loadProfile(employeeCode, { ...profile, ...formData });
      captureEvent(posthog, 'Profile Updated', { employeeCode });
      setMessage("Profile details saved successfully!");
      setMessageType("success");
      setEditMode(false);
    } catch (err) {
      console.error("Profile save error:", err);
      captureError(posthog, 'Profile Update Failed', err, { employeeCode });
      setMessage(err.message || "Network error. Please try again.");
      setMessageType("error");
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEditMode(false);
    setMessage("");
    setMessageType(null);
    if (profile) {
      const resetForm = buildFormState(profile);
      setFormData(resetForm);
      setInitials(getInitials(resetForm.first_name, resetForm.last_name));
    }
  };

  if (loading || !profile) {
    return (
      <DashboardLayout>
        <div className="profile-container">
          <div className="profile-loading">
            <div className="loading-spinner"></div>
            <p>Loading profile...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  const departmentIdForDisplay =
    formData.department_id ||
    profile.department_id ||
    sessionUser?.department_id ||
    "";

  const currentDepartment = departments.find(
    (dept) =>
      dept?.id !== undefined &&
      departmentIdForDisplay !== "" &&
      String(dept.id) === String(departmentIdForDisplay)
  );

  const departmentDisplayName =
    currentDepartment?.name ||
    profile?.department?.name ||
    profile?.department_name ||
    "-";

  const designationIdForDisplay =
    formData.designation_id || profile.designation_id || "";

  const currentDesignation = designations.find(
    (designation) =>
      designation?.id !== undefined &&
      designationIdForDisplay !== "" &&
      String(designation.id) === String(designationIdForDisplay)
  );

  const designationDisplayName =
    currentDesignation?.name ||
    profile?.designation?.name ||
    profile?.designation_name ||
    (designationIdForDisplay ? designationIdForDisplay : "-");

  return (
    <DashboardLayout>
      <div className="profile-container">
        {/* Header Section */}
        <div className="profile-header">
          <div className="profile-header-content">
            <div className="profile-avatar-section">
              <div className="profile-avatar">
                <span className="profile-avatar-text">{initials}</span>
                <div className="profile-avatar-status"></div>
              </div>
              <div className="profile-info">
                <h1 className="profile-name">
                  {formData.first_name || "-"} {formData.last_name || ""}
                </h1>
                <div className="profile-role">
                  <FaBriefcase className="profile-role-icon" />
                  <span>{departmentDisplayName}</span>
                </div>
                <div className="profile-emp-code">
                  <FaIdCard className="profile-emp-icon" />
                  <span>
                    Employee Code:{" "}
                    {profile.employee_code ||
                      sessionUser?.EmpCode ||
                      sessionUser?.employee_code ||
                      "-"}
                  </span>
                </div>
              </div>
            </div>
            <div className="profile-actions">
              {!editMode ? (
                <button className="profile-edit-btn" onClick={handleEdit}>
                  <FaEdit />
                  <span>Edit Profile</span>
                </button>
              ) : (
                <div className="profile-action-buttons">
                  <button
                    className="profile-save-btn"
                    onClick={handleSave}
                    disabled={saving}
                  >
                    <FaSave />
                    <span>{saving ? "Saving..." : "Save"}</span>
                  </button>
                  <button className="profile-cancel-btn" onClick={handleCancel}>
                    <FaTimes />
                    <span>Cancel</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Message Display */}
        {message && (
          <div
            className={`profile-message ${messageType ? messageType : "info"}`}
          >
            {message}
          </div>
        )}

        {/* Main Content */}
        <div className="profile-content">
          <div className="profile-grid">
            {/* Personal Information */}
            <div className="profile-section">
              <div className="profile-section-header">
                <FaUser className="profile-section-icon" />
                <h2>Personal Information</h2>
              </div>
              <div className="profile-fields">
                <div className="profile-field">
                  <label>First Name</label>
                  {editMode ? (
                    <input
                      type="text"
                      value={formData.first_name}
                      onChange={(e) =>
                        handleChange("first_name", e.target.value)
                      }
                      placeholder="Enter First Name"
                      className="profile-input"
                    />
                  ) : (
                    <span className="profile-value">
                      {formData.first_name || "-"}
                    </span>
                  )}
                </div>
                <div className="profile-field">
                  <label>Last Name</label>
                  {editMode ? (
                    <input
                      type="text"
                      value={formData.last_name}
                      onChange={(e) =>
                        handleChange("last_name", e.target.value)
                      }
                      placeholder="Enter Last Name"
                      className="profile-input"
                    />
                  ) : (
                    <span className="profile-value">
                      {formData.last_name || "-"}
                    </span>
                  )}
                </div>
                <div className="profile-field">
                  <label>Date of Birth</label>
                  {editMode ? (
                    <input
                      type="date"
                      value={formData.dob}
                      onChange={(e) => handleChange("dob", e.target.value)}
                      className="profile-input"
                    />
                  ) : (
                    <span className="profile-value">{formData.dob || "-"}</span>
                  )}
                </div>
                <div className="profile-field">
                  <label>Gender</label>
                  {editMode ? (
                    <input
                      type="text"
                      value={formData.gender}
                      onChange={(e) => handleChange("gender", e.target.value)}
                      placeholder="Enter Gender"
                      className="profile-input"
                    />
                  ) : (
                    <span className="profile-value">
                      {formData.gender || "-"}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Work Information */}
            <div className="profile-section">
              <div className="profile-section-header">
                <FaBuilding className="profile-section-icon" />
                <h2>Work Information</h2>
              </div>
              <div className="profile-fields">
                <div className="profile-field">
                  <label>Department</label>
                  {editMode ? (
                    <select
                      className="profile-input"
                      value={formData.department_id}
                      onChange={(e) =>
                        handleChange("department_id", e.target.value)
                      }
                    >
                      <option value="">Select Department</option>
                      {departments.map((dept) => (
                        <option key={dept.id} value={dept.id}>
                          {dept.name}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <span className="profile-value">
                      {departmentDisplayName}
                    </span>
                  )}
                </div>
                <div className="profile-field">
                  <label>Designation</label>
                  {editMode ? (
                    <select
                      className="profile-input"
                      value={formData.designation_id}
                      onChange={(e) =>
                        handleChange("designation_id", e.target.value)
                      }
                    >
                      <option value="">Select Designation</option>
                      {designations.map((designation) => (
                        <option key={designation.id} value={designation.id}>
                          {designation.name}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <span className="profile-value">
                      {designationDisplayName}
                    </span>
                  )}
                </div>
                <div className="profile-field">
                  <label>Joining Date</label>
                  {editMode ? (
                    <input
                      type="date"
                      value={formData.joining_date}
                      onChange={(e) =>
                        handleChange("joining_date", e.target.value)
                      }
                      className="profile-input"
                    />
                  ) : (
                    <span className="profile-value">
                      {formData.joining_date || "-"}
                    </span>
                  )}
                </div>
                <div className="profile-field">
                  <label>Location</label>
                  {editMode ? (
                    <input
                      type="text"
                      value={formData.location}
                      onChange={(e) => handleChange("location", e.target.value)}
                      placeholder="Enter Location"
                      className="profile-input"
                    />
                  ) : (
                    <span className="profile-value">
                      {formData.location || "-"}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Contact & Skills */}
            <div className="profile-section">
              <div className="profile-section-header">
                <FaEnvelope className="profile-section-icon" />
                <h2>Contact & Skills</h2>
              </div>
              <div className="profile-fields">
                <div className="profile-field">
                  <label>Email</label>
                  <span className="profile-value">{profile.email || "-"}</span>
                </div>
                <div className="profile-field">
                  <label>Contact Number</label>
                  {editMode ? (
                    <input
                      type="text"
                      value={formData.contact_no}
                      onChange={(e) =>
                        handleChange("contact_no", e.target.value)
                      }
                      placeholder="Enter Contact Number"
                      className="profile-input"
                    />
                  ) : (
                    <span className="profile-value">
                      {formData.contact_no || "-"}
                    </span>
                  )}
                </div>
                <div className="profile-field">
                  <label>Skills</label>
                  {editMode ? (
                    <textarea
                      value={formData.skills}
                      onChange={(e) => handleChange("skills", e.target.value)}
                      placeholder="Enter Skills"
                      className="profile-textarea"
                      rows={3}
                    />
                  ) : (
                    <span className="profile-value">
                      {formData.skills || "-"}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default MyProfile;
